import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useNavigate, useLocation } from "react-router-dom";

import { IntlContext } from "context";
import { useOurService } from "hooks/service";
import { fetchUserNotes } from "hooks/note";
import type { NoteInterface } from "types/note";
import { Loader } from "ui/Loader";
import Seo from "components/Seo";
import { isProviderAvailable } from "helpers/isProviderAvailable";
import { ProviderAboutCard } from "./components/ProviderAboutCard";
import { ProviderPracticalInfoCard } from "./components/ProviderPracticalInfoCard";
import { ProviderProfileHeader } from "./components/ProviderProfileHeader";
import { ProviderReviewsCard } from "./components/ProviderReviewsCard";
import { ProviderServicesListCard } from "./components/ProviderServicesListCard";
import { ChevronLeft } from "lucide-react";
import { Button } from "components/ui/button";
import { ROUTES, buildRoute } from "routes";

const OurServiceView: React.FC = () => {
    const intl = useContext(IntlContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const backFrom: string | undefined = (state as { from?: string } | null)?.from;
    const backHref = backFrom ?? ROUTES.PROVIDERS;
    const backLabel = backHref.startsWith(ROUTES.PROVIDERS)
        ? intl.formatMessage("provider.view.back_to_providers")
        : intl.formatMessage("common.back");
    const [dataNotes, setDataNotes] = useState<NoteInterface[] | null>(null);
    const { data, isLoading } = useOurService(id);

    useEffect(() => {
        if (!data) {
            return;
        }

        const loadNotes = async () => {
            try {
                const providerId = String(data.provider_id ?? "");
                const notes = await fetchUserNotes(providerId);
                setDataNotes(notes);
            } catch {
                setDataNotes([]);
            }
        };

        void loadNotes();
    }, [data]);

    if (isLoading || !data) {
        return <Loader />;
    }

    const catalogServices = data.provider_services ?? [];
    const canContact = isProviderAvailable(data);

    const fullName = data.provider
        ? `${data.provider.first_name ?? ""}${data.provider.last_name ? " " + data.provider.last_name.charAt(0) + "." : ""}`.trim()
        : "";
    const cityLabel = data.provider?.city;
    const categoryLabel = intl.formatMessage(`category.${data.category.toString().toLocaleLowerCase()}`);

    const seoTitle = [
        fullName || intl.formatMessage("our_services.view.default_name"),
        categoryLabel,
        cityLabel ? `${cityLabel}` : "",
        "Twoghether",
    ]
        .filter(Boolean)
        .join(" — ");
    const seoDescription = data.provider?.description?.slice(0, 160);

    return (
        <React.Fragment>
            <Seo
                title={seoTitle}
                description={seoDescription}
                canonical={buildRoute.provider(data.id)}
                ogType="website"
            />
            <div className="min-h-screen bg-violet-50">
                <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
                    <Button
                        variant="ghost"
                        className="hover:bg-transparent hover:underline"
                        onClick={() => navigate(backHref)}
                    >
                        <ChevronLeft className="h-4 w-4" />
                        {backLabel}
                    </Button>

                    <ProviderProfileHeader service={data} />

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <div className="space-y-6">
                            <ProviderPracticalInfoCard provider={data.provider} />
                            <ProviderServicesListCard
                                catalogServices={catalogServices}
                                provider={data.provider}
                                providerId={data.provider_id}
                                canContact={canContact}
                            />
                        </div>

                        <div className="space-y-6 lg:col-span-2">
                            <ProviderAboutCard provider={data.provider} />
                            <ProviderReviewsCard provider={data.provider} notes={dataNotes} />
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default OurServiceView;
